import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  BarChart3, Users, Stethoscope, Building2, Share2, TrendingUp, TrendingDown,
  ChevronRight, ArrowLeft, RefreshCw, Target, CheckCircle2, XCircle, Clock,
  Search, User, Phone, Mail, Eye, Award, Activity, AlertCircle
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import Pagination from '../../Components/common/Pagination';
import { TableSkeleton } from '../../Components/common/Skeleton';

// ─── Role Tab Config ────────────────────────────────────────────────────────
const ROLE_TABS = [
  {
    role: 'customer_care',
    label: 'CC Agents',
    icon: <Users size={15} />,
    color: 'from-blue-500 to-blue-600',
    lightBg: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200'
  },
  {
    role: 'doctor',
    label: 'Doctors',
    icon: <Stethoscope size={15} />,
    color: 'from-emerald-500 to-teal-600',
    lightBg: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-200'
  },
  {
    role: 'hospital',
    label: 'Hospitals',
    icon: <Building2 size={15} />,
    color: 'from-purple-500 to-violet-600',
    lightBg: 'bg-purple-50',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-200'
  },
  {
    role: 'marketing_agent',
    label: 'Marketing',
    icon: <Share2 size={15} />,
    color: 'from-amber-500 to-orange-500',
    lightBg: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200'
  },
];

// ─── Individual Report View ──────────────────────────────────────────────────
const IndividualReport = ({ userId, roleMeta, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchIndividual = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/leads/performance-reports?userId=${userId}`);
        if (res.data?.success) setData(res.data.data);
        else toast.error('Failed to load individual report');
      } catch (err) {
        console.error(err);
        toast.error('Failed to load individual report');
      } finally {
        setLoading(false);
      }
    };
    fetchIndividual();
  }, [userId]);

  if (loading) return (
    <div className="space-y-6">
      {/* Skeleton Header */}
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-slate-100 animate-pulse rounded-2xl" />
        <div className="space-y-2 flex-1">
          <div className="h-6 w-48 bg-slate-100 animate-pulse rounded-xl" />
          <div className="h-3 w-32 bg-slate-100 animate-pulse rounded-lg" />
        </div>
      </div>
      {/* Skeleton Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array(4).fill(0).map((_, i) => (
          <div key={i} className="bg-white rounded-[20px] p-5 border border-slate-100 shadow-sm space-y-3 relative overflow-hidden">
            <div className="absolute inset-0 bg-slate-50/50 animate-pulse" />
            <div className="w-9 h-9 bg-slate-100 rounded-xl" />
            <div className="h-7 w-12 bg-slate-100 rounded-lg" />
            <div className="h-3 w-16 bg-slate-100 rounded-lg" />
          </div>
        ))}
      </div>
      {/* Skeleton Table */}
      <div className="bg-white rounded-[24px] p-6 border border-slate-100 shadow-sm">
        <TableSkeleton rows={4} />
      </div>
    </div>
  );

  if (!data) return null;

  const { user, stats, leads, extraMetrics = {} } = data;
  const conversionRate = stats.total > 0 ? Math.round((stats.converted / stats.total) * 100) : 0;
  const isDoctor = user.role === 'doctor';

  const statCards = [
    { label: 'Total Leads', value: stats.total, icon: <Activity size={18} />, color: 'from-[#143E78] to-blue-500', lightBg: 'bg-blue-50', textColor: 'text-blue-700' },
    { label: 'Pending', value: stats.pending, icon: <Clock size={18} />, color: 'from-amber-500 to-orange-400', lightBg: 'bg-amber-50', textColor: 'text-amber-700' },
    { label: 'Converted', value: stats.converted, icon: <CheckCircle2 size={18} />, color: 'from-emerald-500 to-teal-500', lightBg: 'bg-emerald-50', textColor: 'text-emerald-700' },
    { label: 'Lost', value: stats.lost, icon: <XCircle size={18} />, color: 'from-red-400 to-rose-500', lightBg: 'bg-red-50', textColor: 'text-red-700' },
  ];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Back Button + Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-2xl transition-all shadow-sm"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-xl font-black text-slate-900 tracking-tight">
              {user.firstName} {user.lastName}
            </h2>
            {extraMetrics.specialization && (
              <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-100 text-[9px] font-black uppercase tracking-widest rounded-xl">
                {extraMetrics.specialization}
              </span>
            )}
            {extraMetrics.doctorType && (
              <span className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-100 text-[9px] font-black uppercase tracking-widest rounded-xl">
                {extraMetrics.doctorType}
              </span>
            )}
          </div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5">
            Individual Performance Report · {user.role?.replace(/_/g, ' ')}
          </p>
        </div>
      </div>

      {/* Stat Cards */}
      <div className={`grid gap-4 ${isDoctor ? 'grid-cols-2 md:grid-cols-5' : 'grid-cols-2 md:grid-cols-4'}`}>
        {statCards.map(card => (
          <div key={card.label} className="bg-white rounded-[20px] p-5 shadow-lg shadow-slate-200/30 border border-slate-100 relative overflow-hidden">
            <div className={`absolute inset-0 bg-gradient-to-br ${card.color} opacity-[0.04]`} />
            <div className={`w-9 h-9 ${card.lightBg} ${card.textColor} rounded-xl flex items-center justify-center mb-3`}>{card.icon}</div>
            <div className="text-2xl font-black text-slate-900">{card.value}</div>
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">{card.label}</div>
          </div>
        ))}

        {/* Doctor Earnings Card */}
        {isDoctor && (
          <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-[20px] p-5 shadow-lg shadow-teal-200/40 relative overflow-hidden">
            <div className="absolute -right-3 -top-3 w-16 h-16 bg-white/10 rounded-full" />
            <div className="w-9 h-9 bg-white/20 text-white rounded-xl flex items-center justify-center mb-3">
              <Award size={18} />
            </div>
            <div className="text-2xl font-black text-white">
              ₹{(extraMetrics.totalEarnings || 0).toLocaleString('en-IN')}
            </div>
            <div className="text-[10px] font-black text-white/70 uppercase tracking-widest mt-1">Total Earnings</div>
          </div>
        )}
      </div>

      {/* Conversion Bar */}
      <div className="bg-white rounded-[24px] p-6 shadow-lg shadow-slate-200/30 border border-slate-100">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-black text-slate-900">Conversion Rate</span>
          <span className={`text-2xl font-black ${conversionRate >= 50 ? 'text-emerald-600' : conversionRate >= 25 ? 'text-amber-600' : 'text-red-500'}`}>{conversionRate}%</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#143E78] to-blue-400 transition-all duration-700"
            style={{ width: `${conversionRate}%` }}
          />
        </div>
        <div className="flex justify-between mt-2">
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">0%</span>
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">100%</span>
        </div>
      </div>

      {/* Leads List */}
      <div className="bg-white rounded-[24px] p-6 shadow-lg shadow-slate-200/30 border border-slate-100">
        <h3 className="text-sm font-black text-slate-900 mb-5 uppercase tracking-widest">Associated Leads</h3>
        {leads.length === 0 ? (
          <div className="py-12 text-center">
            <AlertCircle className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-xs font-black text-slate-500 uppercase tracking-wider">No leads found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-100">
                  {['Patient', 'Booking ID', 'Status', 'Source', 'Date'].map(h => (
                    <th key={h} className="pb-3 text-[9px] font-black text-slate-400 uppercase tracking-widest">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {leads.map(lead => (
                  <tr key={lead._id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-3 text-sm font-bold text-slate-900">{lead.patientName}</td>
                    <td className="py-3 text-[10px] font-semibold text-slate-400">{lead.bookingId || '—'}</td>
                    <td className="py-3">
                      <StatusBadge status={lead.status} />
                    </td>
                    <td className="py-3 text-[10px] font-semibold text-slate-500 uppercase">{lead.source || 'Direct'}</td>
                    <td className="py-3 text-[10px] font-semibold text-slate-400">
                      {new Date(lead.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};


// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    new: 'bg-blue-50 text-blue-700',
    contacted: 'bg-cyan-50 text-cyan-700',
    qualified: 'bg-indigo-50 text-indigo-700',
    screening: 'bg-violet-50 text-violet-700',
    scheduled: 'bg-purple-50 text-purple-700',
    confirmed: 'bg-amber-50 text-amber-700',
    paid: 'bg-lime-50 text-lime-700',
    surgery: 'bg-emerald-50 text-emerald-700',
    closure: 'bg-teal-50 text-teal-700',
    completed: 'bg-green-50 text-green-700',
    not_interested: 'bg-red-50 text-red-600',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[8px] font-black uppercase tracking-wider rounded-lg ${map[status] || 'bg-slate-100 text-slate-600'}`}>
      {status?.replace(/_/g, ' ') || 'unknown'}
    </span>
  );
};

// ─── Agent/Entity Row ─────────────────────────────────────────────────────────
const EntityRow = ({ item, roleMeta, onViewIndividual, currentRole }) => {
  const { metrics } = item;
  const rate = currentRole === 'customer_care'
    ? metrics.conversionRate
    : currentRole === 'marketing_agent'
      ? metrics.conversionRate
      : null;

  return (
    <tr className="hover:bg-slate-50/60 transition-colors group">
      <td className="py-4 pr-4">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl ${roleMeta.lightBg} ${roleMeta.textColor} flex items-center justify-center font-black text-sm flex-shrink-0`}>
            {(item.name || '?').charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-black text-slate-900">{item.name || 'N/A'}</div>
            {item.specialization && (
              <div className="text-[9px] font-semibold text-slate-400 uppercase">{item.specialization}</div>
            )}
            {item.city && (
              <div className="text-[9px] font-semibold text-slate-400 uppercase">{item.city}</div>
            )}
          </div>
        </div>
      </td>
      <td className="py-4 pr-4">
        <div className="text-xs font-semibold text-slate-500">{item.email}</div>
        <div className="text-[10px] font-semibold text-slate-400">{item.phone}</div>
      </td>

      {/* Role-specific metrics */}
      {currentRole === 'customer_care' && (
        <>
          <td className="py-4 pr-4 text-sm font-black text-slate-900">{metrics.totalLeads ?? 0}</td>
          <td className="py-4 pr-4 text-sm font-black text-amber-600">{metrics.pendingLeads ?? 0}</td>
          <td className="py-4 pr-4 text-sm font-black text-emerald-600">{metrics.convertedLeads ?? 0}</td>
          <td className="py-4 pr-4">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden max-w-[80px]">
                <div
                  className="h-full bg-gradient-to-r from-[#143E78] to-blue-400 rounded-full"
                  style={{ width: `${rate}%` }}
                />
              </div>
              <span className={`text-xs font-black ${rate >= 50 ? 'text-emerald-600' : rate >= 25 ? 'text-amber-600' : 'text-red-500'}`}>{rate}%</span>
            </div>
          </td>
        </>
      )}

      {currentRole === 'doctor' && (
        <>
          <td className="py-4 pr-4 text-sm font-black text-slate-900">{metrics.totalReferred ?? 0}</td>
          <td className="py-4 pr-4 text-sm font-black text-emerald-600">{metrics.totalSurgeries ?? 0}</td>
          <td className="py-4 pr-4">
            <span className="text-sm font-black text-teal-700">
              ₹{(metrics.earnings || 0).toLocaleString('en-IN')}
            </span>
          </td>
        </>
      )}

      {currentRole === 'hospital' && (
        <>
          <td className="py-4 pr-4 text-sm font-black text-slate-900">{metrics.totalMatched ?? 0}</td>
          <td className="py-4 pr-4 text-sm font-black text-purple-600">{metrics.totalReferred ?? 0}</td>
          <td className="py-4 pr-4 text-sm font-black text-emerald-600">{metrics.surgeriesCount ?? 0}</td>
        </>
      )}

      {currentRole === 'marketing_agent' && (
        <>
          <td className="py-4 pr-4 text-sm font-black text-slate-900">{metrics.totalReferred ?? 0}</td>
          <td className="py-4 pr-4 text-sm font-black text-emerald-600">{metrics.converted ?? 0}</td>
          <td className="py-4 pr-4">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden max-w-[80px]">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-orange-400 rounded-full"
                  style={{ width: `${rate}%` }}
                />
              </div>
              <span className={`text-xs font-black ${rate >= 50 ? 'text-emerald-600' : rate >= 25 ? 'text-amber-600' : 'text-red-500'}`}>{rate}%</span>
            </div>
          </td>
        </>
      )}

      <td className="py-4">
        {item.userId && (
          <button
            onClick={() => onViewIndividual(item.userId)}
            className={`p-2 ${roleMeta.lightBg} ${roleMeta.textColor} ${roleMeta.borderColor} border rounded-xl hover:opacity-80 transition-all group-hover:shadow-sm`}
            title="View individual report"
          >
            <Eye size={13} />
          </button>
        )}
      </td>
    </tr>
  );
};

// ─── Column headers per role ──────────────────────────────────────────────────
const COLUMNS = {
  customer_care: ['Agent', 'Contact', 'Total', 'Pending', 'Converted', 'Rate', ''],
  doctor: ['Doctor', 'Contact', 'Total Cases', 'Surgery Cases', 'Earnings', ''],
  hospital: ['Hospital', 'Contact', 'Matched', 'Referred', 'Surgeries', ''],
  marketing_agent: ['Agent', 'Contact', 'Referred', 'Converted', 'Rate', ''],
};

// ─── Summary KPI bar ──────────────────────────────────────────────────────────
const SummaryBar = ({ data, currentRole }) => {
  if (!data || data.length === 0) return null;

  const kpis = (() => {
    if (currentRole === 'customer_care') {
      const total = data.reduce((s, d) => s + (d.metrics.totalLeads || 0), 0);
      const pending = data.reduce((s, d) => s + (d.metrics.pendingLeads || 0), 0);
      const converted = data.reduce((s, d) => s + (d.metrics.convertedLeads || 0), 0);
      const rate = total > 0 ? Math.round((converted / total) * 100) : 0;
      return [
        { label: 'Total Leads', value: total, icon: <Activity size={16} />, color: 'text-[#143E78]' },
        { label: 'Active / Pending', value: pending, icon: <Clock size={16} />, color: 'text-amber-600' },
        { label: 'Converted', value: converted, icon: <CheckCircle2 size={16} />, color: 'text-emerald-600' },
        { label: 'Avg Conversion', value: `${rate}%`, icon: <Target size={16} />, color: rate >= 50 ? 'text-emerald-600' : 'text-amber-600' },
      ];
    }
    if (currentRole === 'doctor') {
      const referred = data.reduce((s, d) => s + (d.metrics.totalReferred || 0), 0);
      const surgeries = data.reduce((s, d) => s + (d.metrics.totalSurgeries || 0), 0);
      const earnings = data.reduce((s, d) => s + (d.metrics.earnings || 0), 0);
      return [
        { label: 'Total Cases Assigned', value: referred, icon: <Share2 size={16} />, color: 'text-[#143E78]' },
        { label: 'Surgery Cases', value: surgeries, icon: <Activity size={16} />, color: 'text-emerald-600' },
        { label: 'Total Earnings', value: `₹${earnings.toLocaleString('en-IN')}`, icon: <Award size={16} />, color: 'text-teal-600' },
        { label: 'Doctors', value: data.length, icon: <Stethoscope size={16} />, color: 'text-indigo-600' },
      ];
    }
    if (currentRole === 'hospital') {
      const matched = data.reduce((s, d) => s + (d.metrics.totalMatched || 0), 0);
      const surgeries = data.reduce((s, d) => s + (d.metrics.surgeriesCount || 0), 0);
      return [
        { label: 'Total Leads Matched', value: matched, icon: <Building2 size={16} />, color: 'text-[#143E78]' },
        { label: 'Surgeries Done', value: surgeries, icon: <CheckCircle2 size={16} />, color: 'text-purple-600' },
        { label: 'Hospitals', value: data.length, icon: <Building2 size={16} />, color: 'text-indigo-600' },
      ];
    }
    if (currentRole === 'marketing_agent') {
      const referred = data.reduce((s, d) => s + (d.metrics.totalReferred || 0), 0);
      const converted = data.reduce((s, d) => s + (d.metrics.converted || 0), 0);
      const rate = referred > 0 ? Math.round((converted / referred) * 100) : 0;
      return [
        { label: 'Total Referred', value: referred, icon: <Share2 size={16} />, color: 'text-[#143E78]' },
        { label: 'Converted', value: converted, icon: <CheckCircle2 size={16} />, color: 'text-emerald-600' },
        { label: 'Avg Rate', value: `${rate}%`, icon: <TrendingUp size={16} />, color: rate >= 50 ? 'text-emerald-600' : 'text-amber-600' },
        { label: 'Agents', value: data.length, icon: <Users size={16} />, color: 'text-amber-600' },
      ];
    }
    return [];
  })();

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {kpis.map(kpi => (
        <div key={kpi.label} className="bg-white rounded-[20px] p-5 shadow-md shadow-slate-200/30 border border-slate-100 flex items-center gap-4">
          <div className={`${kpi.color} opacity-80`}>{kpi.icon}</div>
          <div>
            <div className={`text-xl font-black ${kpi.color}`}>{kpi.value}</div>
            <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{kpi.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PerformanceReports = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const roleFromUrl = searchParams.get('role') || 'customer_care';
  const userIdFromUrl = searchParams.get('userId');

  const [activeRole, setActiveRole] = useState(roleFromUrl);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [individualUserId, setIndividualUserId] = useState(userIdFromUrl || null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const roleMeta = ROLE_TABS.find(t => t.role === activeRole) || ROLE_TABS[0];

  const fetchReports = useCallback(async (role) => {
    setLoading(true);
    try {
      const res = await api.get(`/leads/performance-reports?role=${role}`);
      if (res.data?.success) {
        const data = res.data.data;
        setReportData(Array.isArray(data) ? data : []);
      } else {
        setReportData([]);
        toast.error('Failed to load performance reports');
      }
    } catch (err) {
      console.error(err);
      setReportData([]);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!individualUserId) {
      fetchReports(activeRole);
    }
  }, [activeRole, individualUserId, fetchReports]);

  const handleTabChange = (role) => {
    setActiveRole(role);
    setIndividualUserId(null);
    setSearch('');
    setCurrentPage(1);
    setSearchParams({ role });
  };

  const handleViewIndividual = (userId) => {
    setIndividualUserId(userId);
    setSearchParams({ role: activeRole, userId });
  };

  const handleBack = () => {
    setIndividualUserId(null);
    setSearchParams({ role: activeRole });
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const filteredData = reportData.filter(item =>
    item.name?.toLowerCase().includes(search.toLowerCase()) ||
    item.email?.toLowerCase().includes(search.toLowerCase()) ||
    item.city?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1;
  const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const columns = COLUMNS[activeRole] || [];

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">


      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${roleMeta.color} flex items-center justify-center text-white shadow-lg`}>
              <BarChart3 size={18} />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">Performance Reports</h2>
              <p className="text-slate-400 font-bold uppercase text-[9px] sm:text-[10px] tracking-widest mt-0.5">
                Individual & aggregate performance analytics by role
              </p>
            </div>
          </div>
        </div>
        {!individualUserId && (
          <button
            onClick={() => fetchReports(activeRole)}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-3 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white font-black text-xs uppercase tracking-wider rounded-2xl transition-all self-start md:self-auto shadow-md"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        )}
      </div>

      {/* If viewing an individual, show the individual report */}
      {individualUserId ? (
        <IndividualReport userId={individualUserId} roleMeta={roleMeta} onBack={handleBack} />
      ) : (
        <>
          {/* Role Tabs */}
          <div className="flex gap-2 flex-wrap">
            {ROLE_TABS.map(tab => (
              <button
                key={tab.role}
                onClick={() => handleTabChange(tab.role)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-wider transition-all duration-200 ${activeRole === tab.role
                    ? `bg-gradient-to-r ${tab.color} text-white shadow-lg scale-105`
                    : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300 hover:text-slate-700'
                  }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Summary KPI Bar */}
          <SummaryBar data={reportData} currentRole={activeRole} />

          {/* Search */}
          <div className="bg-white rounded-[24px] p-4 shadow-lg shadow-slate-200/30 border border-slate-100">
            <div className="relative max-w-sm">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder={`Search ${roleMeta.label.toLowerCase()}...`}
                value={search}
                onChange={handleSearchChange}
                className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-[#143E78]/20 focus:border-[#143E78] transition-all"
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-[24px] sm:rounded-[32px] p-6 sm:p-8 shadow-2xl shadow-slate-200/40 border border-slate-100">
            {loading ? (
              <TableSkeleton rows={8} />
            ) : filteredData.length === 0 ? (
              <div className="py-16 text-center bg-slate-50 rounded-3xl border-2 border-dashed border-slate-100">
                <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h4 className="text-sm font-black text-slate-800 uppercase tracking-wider">No Data Found</h4>
                <p className="text-xs text-slate-400 font-semibold mt-1">
                  {search ? 'No results for your search.' : `No ${roleMeta.label.toLowerCase()} found.`}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto w-full">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100">
                      {columns.map(col => (
                        <th key={col} className="pb-4 pr-4 text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {paginatedData.map((item, idx) => (
                      <EntityRow
                        key={item.userId || idx}
                        item={item}
                        roleMeta={roleMeta}
                        currentRole={activeRole}
                        onViewIndividual={handleViewIndividual}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {filteredData.length > 0 && totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center sm:text-left order-2 sm:order-1">
                Displaying {filteredData.length > 0 ? (currentPage - 1) * pageSize + 1 : 0}-{Math.min(currentPage * pageSize, filteredData.length)} of {filteredData.length} records
              </div>

              <div className="flex w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0 order-1 sm:order-2">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PerformanceReports;